import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Box,
    Flex,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useMultiStyleConfig} from '@chakra-ui/react'
import {ChevronDownType2, CheckMarkIcon, ErrorIcon} from '../icons'

const WebFloatingSelect = ({
    name,
    label,
    secondaryLabel,
    options = [],
    rules = {},
    error,
    control,
    defaultValue,
    webHelperText,
    webHelperIcon: WebHelperIcon,
    webHelperStyle,
    hideRequiredIndicator = false,
    formControlProps,
    showSelectedCheckmark = false,
    displayValueAsLabel = false
}) => {
    const styles = useMultiStyleConfig('WebFloatingSelect', {})
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const isRequired = !!rules?.required
    const isDisabled = !!formControlProps?.isDisabled

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value}}) => {
                const selectedLabel = displayValueAsLabel ? (value || '') : options.find((o) => o.value === value)?.label
                const isFloated = isOpen || !!value

                const labelSx = {
                    ...styles.label,
                    ...(isFloated ? styles.labelFloated : {}),
                    ...(error
                        ? {color: 'webTypo.textError'}
                        : {}),
                    ...(isDisabled
                        ? {
                              color: 'webTypo.textDisabled',
                              '.web-field-suffix': {color: 'webTypo.textDisabled'},
                              '.web-field-optional': {color: 'webTypo.textDisabled'}
                          }
                        : {})
                }

                const iconColor = isDisabled ? 'webTypo.textDisabled' : 'webTypo.textPrimary'

                return (
                    <FormControl isInvalid={!!error} {...formControlProps}>
                        <Box position="relative" ref={containerRef}>
                            {/* Floating label */}
                            <FormLabel sx={labelSx}>
                                {label}
                                {isRequired && !hideRequiredIndicator && (
                                    <span className="web-field-suffix"> *</span>
                                )}
                                {secondaryLabel && (
                                    <span className="web-field-optional"> {secondaryLabel}</span>
                                )}
                            </FormLabel>

                            {/* Trigger button */}
                            <Box
                                as="button"
                                type="button"
                                __css={styles.trigger}
                                data-invalid={error ? true : undefined}
                                onClick={!isDisabled ? () => setIsOpen((prev) => !prev) : undefined}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setIsOpen(false)
                                }}
                                disabled={isDisabled}
                                aria-expanded={isOpen}
                                aria-haspopup="listbox"
                            >
                                <Text as="span" sx={styles.triggerText}>
                                    {selectedLabel || ''}
                                </Text>
                                <ChevronDownType2
                                    sx={{
                                        ...styles.chevron,
                                        transform: isOpen
                                            ? 'translateY(-50%) rotate(180deg)'
                                            : 'translateY(-50%) rotate(0deg)',
                                        transition: 'transform 0.2s ease-in-out'
                                    }}
                                    color={iconColor}
                                />
                            </Box>

                            {/* Dropdown list */}
                            {isOpen && (
                                <Box role="listbox" __css={styles.dropdown}>
                                    {options.map((opt) => {
                                        const isSelected = value === opt.value
                                        return (
                                            <Box
                                                key={`${opt.label}-${opt.value}`}
                                                role="option"
                                                aria-selected={isSelected}
                                                __css={styles.option}
                                                onClick={() => {
                                                    onChange(opt.value)
                                                    setIsOpen(false)
                                                }}
                                            >
                                                <Text sx={styles.optionText}>{opt.label}</Text>
                                                {showSelectedCheckmark && isSelected && (
                                                    <CheckMarkIcon sx={styles.checkIcon} />
                                                )}
                                            </Box>
                                        )
                                    })}
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <FormErrorMessage sx={styles.errorMessage}>
                                <ErrorIcon w="13px" h="13px" color="webTypo.textError" flexShrink={0} />
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {webHelperText && !error && (
                            <Flex __css={styles.helperText} {...webHelperStyle}>
                                {WebHelperIcon && (
                                    <WebHelperIcon w="12px" h="12px" flexShrink={0} />
                                )}
                                {webHelperText}
                            </Flex>
                        )}
                    </FormControl>
                )
            }}
        />
    )
}

WebFloatingSelect.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    secondaryLabel: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    webHelperText: PropTypes.string,
    webHelperIcon: PropTypes.elementType,
    webHelperStyle: PropTypes.object,
    hideRequiredIndicator: PropTypes.bool,
    formControlProps: PropTypes.object,
    showSelectedCheckmark: PropTypes.bool,
    displayValueAsLabel: PropTypes.bool
}

export default WebFloatingSelect
